import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../lib/auth';
import { PackIcon } from '../components/PackIcon';
import { ArrowLeft, ArrowRight, UploadCloud, CheckCircle2, Info, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const Step = ({ n, active, done, label, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <div className={`w-8 h-8 border grid place-items-center font-mono text-xs ${active ? 'border-accent text-accent' : done ? 'border-foreground text-foreground' : 'border-border text-muted-foreground'}`}>
      {String(n).padStart(2, '0')}
    </div>
    <span className={`text-sm ${active || done ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
  </div>
);

const StepConnector = () => <div className="flex-1 h-px bg-border" />;

const Stepper = ({ step }) => (
  <div className="hidden md:flex items-center gap-3 px-6 md:px-10 py-4 border-b border-border bg-surface" data-testid="audit-stepper">
    <Step n={1} testid="stepper-level" active={step === 1} done={step > 1} label="Packaging level" />
    <StepConnector />
    <Step n={2} testid="stepper-format" active={step === 2} done={step > 2} label="Pack format" />
    <StepConnector />
    <Step n={3} testid="stepper-upload" active={step === 3} done={step > 3} label="Upload & context" />
    <StepConnector />
    <Step n={4} testid="stepper-audit" active={step === 4} done={step > 4} label="Audit" />
  </div>
);

// Step 0A
const LevelCard = ({ id, title, desc, examples, rules, active, onSelect }) => (
  <button
    onClick={() => onSelect(id)}
    data-testid={`level-card-${id}`}
    className={`text-left p-8 border bg-card transition-colors hover-lift relative ${active ? 'border-gradient-pt' : 'border-border'}`}
  >
    <div className="label-mono mb-2">{id.toUpperCase()}</div>
    <div className="text-2xl font-medium tracking-tight">{title}</div>
    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{desc}</p>
    <div className="mt-6">
      <div className="label-mono mb-1">Examples</div>
      <div className="text-sm">{examples}</div>
    </div>
    <div className="mt-4 pt-4 border-t border-border">
      <div className="label-mono mb-1">FSSAI rules</div>
      <div className="text-sm">{rules}</div>
    </div>
  </button>
);

export default function AuditNew() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get('resume');
  const [step, setStep] = useState(1);
  const [packagingLevel, setPackagingLevel] = useState('');
  const [formatId, setFormatId] = useState('');
  const [groups, setGroups] = useState([]);
  const fileRef = useRef(null);
  const summaryRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [auditId, setAuditId] = useState('');
  const [preAnalysis, setPreAnalysis] = useState(null);
  const [expectedPanels, setExpectedPanels] = useState([]);
  const [pageCount, setPageCount] = useState(0);

  const [ctx, setCtx] = useState({
    product_category: 'General Foods',
    net_weight: '',
    net_weight_unit: 'g',
    pdp_width_mm: '',
    pdp_height_mm: '',
    is_imported: false,
    is_ecommerce: false,
    pack_type: '',
    material_specs: '',
  });

  const [running, setRunning] = useState(false);
  const [statusInfo, setStatusInfo] = useState({ step: 0, message: '' });
  const [bundlePicker, setBundlePicker] = useState({ open: false, bundles: [] });
  const aliveRef = useRef(true);

  useEffect(() => () => { aliveRef.current = false; }, []);

  useEffect(() => {
    api.get('/pack-formats').then(({ data }) => setGroups(data.groups || [])).catch(() => {});
  }, []);

  // Resume a draft (preanalyzed audit) — load its state and jump to Step 3.
  useEffect(() => {
    if (!resumeId) return;
    (async () => {
      try {
        const { data } = await api.get(`/audit/${resumeId}`);
        if (!data) return;
        const pc = data.pack_config || {};
        if (pc.packaging_level) setPackagingLevel(pc.packaging_level);
        if (pc.pack_format_id) setFormatId(pc.pack_format_id);
        if (data.context) setCtx((prev) => ({ ...prev, ...data.context }));
        setAuditId(data.audit_id);
        setPreAnalysis(data.pre_analysis || null);
        setPageCount(data.page_count || 0);
        setStep(3);
        toast.info(`Resumed draft · ${data.file_name}`);
      } catch (e) {
        toast.error('Could not load draft audit');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId]);

  // Super-admin lives in the admin console only.
  const blockSuperAdmin = user?.role === 'super_admin';

  const selectedFormat = useMemo(() => {
    for (const g of groups) for (const f of (g.formats || [])) if (f.id === formatId) return f;
    return null;
  }, [groups, formatId]);

  const next = () => setStep((s) => Math.min(4, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  // ----- handlers
  const onLevelSelect = (lvl) => {
    setPackagingLevel(lvl);
    setStep(2);
  };

  const onFormatSelect = (id) => {
    setFormatId(id);
    // Auto-scroll the summary card / Continue button into view after state settles.
    requestAnimationFrame(() => {
      setTimeout(() => {
        summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const submitUpload = async () => {
    if (!file) { toast.error('Choose a PDF artwork'); return; }
    if (!packagingLevel || !formatId) { toast.error('Pack configuration missing'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('packaging_level', packagingLevel);
      form.append('pack_format_id', formatId);
      const { data } = await api.post('/audit/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAuditId(data.audit_id);
      setPreAnalysis(data.pre_analysis);
      setExpectedPanels(data.expected_panels || []);
      setPageCount(data.page_count || 0);
      toast.success(`Pre-analysis complete (${data.page_count} pages)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const startAudit = async (bundleIdOverride = null) => {
    if (!auditId) { toast.error('Upload PDF first'); return; }
    // Pre-flight: load credits — if user has multiple paid bundles, force them to choose.
    if (user && !['admin', 'super_admin'].includes(user.role) && !bundleIdOverride) {
      try {
        const { data } = await api.get('/account/credits');
        const paid = (data.bundles || []).filter((b) => !['free_signup', 'free_internal_monthly'].includes(b.type));
        const free = (data.bundles || []).find((b) => ['free_signup', 'free_internal_monthly'].includes(b.type));
        if (data.total_active === 0) {
          toast.error('No credits available. Buy a plan to continue.', { duration: 6000 });
          nav('/pricing');
          return;
        }
        if (!free && paid.length > 1) {
          setBundlePicker({ open: true, bundles: paid });
          return;
        }
      } catch (e) { /* fall through */ }
    }
    setRunning(true);
    setStep(4);
    try {
      const payload = { ...ctx };
      payload.pdp_width_mm = ctx.pdp_width_mm ? Number(ctx.pdp_width_mm) : null;
      payload.pdp_height_mm = ctx.pdp_height_mm ? Number(ctx.pdp_height_mm) : null;
      const url = bundleIdOverride
        ? `/audit/run/${auditId}?credit_bundle_id=${encodeURIComponent(bundleIdOverride)}`
        : `/audit/run/${auditId}`;
      await api.post(url, payload);
      // poll
      const tick = async () => {
        if (!aliveRef.current) return;
        try {
          const { data } = await api.get(`/audit/${auditId}/status`);
          if (!aliveRef.current) return;
          setStatusInfo({ step: data.progress_step, message: data.progress_message });
          if (data.status === 'complete') {
            nav(`/audit/${auditId}`);
            return;
          }
          if (data.status === 'error') {
            toast.error(`Audit failed: ${data.error_message || 'unknown error'}`);
            setRunning(false);
            return;
          }
          setTimeout(tick, 2000);
        } catch (e) {
          if (aliveRef.current) setTimeout(tick, 3000);
        }
      };
      tick();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start audit');
      setRunning(false);
    }
  };

  return (
    <>
    <Layout>
      {blockSuperAdmin && <Navigate to="/admin" replace />}
      <Stepper step={step} />
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-8 md:py-12">

        {/* STEP 1 — Level */}
        {step === 1 && (
          <div data-testid="step-level" className="animate-fade-up">
            <div className="label-mono mb-4">Step 01 / Packaging level</div>
            <h1 className="text-3xl md:text-4xl font-medium tracking-tight mb-3">What level of packaging are you checking?</h1>
            <p className="text-muted-foreground max-w-2xl mb-12">FSSAI labelling requirements differ across primary, secondary, and tertiary packs. This determines which mandatory fields and structural checks apply.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border">
              <LevelCard
                id="primary"
                active={packagingLevel === 'primary'}
                onSelect={onLevelSelect}
                title="Primary"
                desc="The pack that directly contains the food."
                examples="Pouch, bottle, can, jar, sachet, wrapper"
                rules="Full FSSAI labelling rules — 13 mandatory fields"
              />
              <LevelCard
                id="secondary"
                active={packagingLevel === 'secondary'}
                onSelect={onLevelSelect}
                title="Secondary"
                desc="Groups or contains primary packs."
                examples="Multipack box, display tray, gift box, sleeve over bottles"
                rules="Conditional — depends on whether sold as a unit"
              />
              <LevelCard
                id="tertiary"
                active={packagingLevel === 'tertiary'}
                onSelect={onLevelSelect}
                title="Tertiary"
                desc="Outer transit / bulk packaging."
                examples="Corrugated master carton, shrink wrap tray, pallet wrap"
                rules="Minimal FSSAI requirements (traceability)"
              />
            </div>
          </div>
        )}

        {/* STEP 2 — Format (+ Secondary clarification + summary) */}
        {step === 2 && (
          <div data-testid="step-format" className="animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-mono">Step 02 / Pack format</div>
                <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">What type of pack is this?</h1>
              </div>
              <button onClick={back} data-testid="back-to-level" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft size={14} strokeWidth={1.5} /> Change level
              </button>
            </div>

            <div className="space-y-12">
              {groups.map((g) => (
                <div key={g.name}>
                  <div className="flex items-baseline justify-between mb-4">
                    <div className="label-mono">{g.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">{g.formats.length} formats</div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-border">
                    {g.formats.map((f) => (
                      <button
                        key={f.id}
                        onClick={() => onFormatSelect(f.id)}
                        data-testid={`format-card-${f.id}`}
                        className={`bg-card p-5 text-left transition-colors flex flex-col gap-3 hover-lift relative ${formatId === f.id ? 'border-gradient-pt' : 'border border-transparent'}`}
                      >
                        <div className="text-foreground">
                          <PackIcon formatId={f.id} size={48} />
                        </div>
                        <div className="space-y-1">
                          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{f.id}</div>
                          <div className="font-medium text-sm leading-snug">{f.name}</div>
                          <div className="text-xs text-muted-foreground line-clamp-2">{f.descriptor}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            {selectedFormat && (
              <div ref={summaryRef} className="mt-12 panel p-8" data-testid="summary-card">
                <div className="label-mono mb-3">Your pack configuration</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <div className="label-mono-sm">Level</div>
                    <div className="text-base font-medium capitalize">{packagingLevel}</div>
                  </div>
                  <div>
                    <div className="label-mono-sm">Format</div>
                    <div className="text-base font-medium">{selectedFormat.name} <span className="text-muted-foreground font-mono text-xs">{selectedFormat.id}</span></div>
                  </div>
                  <div>
                    <div className="label-mono-sm">Expected panels</div>
                    <div className="font-mono text-xs text-muted-foreground">{(selectedFormat.expected_panels || []).join(' · ')}</div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end">
                  <button onClick={() => setStep(3)} data-testid="continue-to-upload" className="inline-flex items-center gap-2 h-11 px-6 bg-foreground text-background hover:opacity-90">
                    Continue <ArrowRight size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 3 — Upload + Context */}
        {step === 3 && (
          <div data-testid="step-upload" className="animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-mono">Step 03 / Upload &amp; context</div>
                <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Upload your artwork PDF</h1>
              </div>
              <button onClick={back} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-format">
                <ArrowLeft size={14} strokeWidth={1.5} /> Change format
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <div
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                  data-drag={dragging}
                  className="dashed-zone p-12 text-center cursor-pointer min-h-[280px] flex flex-col items-center justify-center"
                  data-testid="upload-dropzone"
                >
                  <UploadCloud size={32} strokeWidth={1.5} className="text-muted-foreground" />
                  <div className="mt-4 font-medium">Drop a print-ready artwork PDF</div>
                  <div className="text-xs text-muted-foreground mt-1">or click to browse · max 50 MB · multi-page allowed</div>
                  {file && (
                    <div className="mt-6 font-mono text-xs px-3 py-2 border border-border bg-surface">{file.name} · {(file.size/1024/1024).toFixed(2)} MB</div>
                  )}
                  <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="file-input" />
                </div>

                <div className="mt-6 flex items-center gap-3">
                  <Button onClick={submitUpload} disabled={!file || uploading} data-testid="run-preanalysis-btn" className="rounded-none">
                    {uploading ? 'Analysing…' : 'Run pre-analysis'}
                  </Button>
                  {auditId && <span className="font-mono text-xs text-muted-foreground">audit_id · {auditId}</span>}
                </div>

                {preAnalysis && (
                  <div className="mt-8 panel p-6 space-y-4" data-testid="preanalysis-card">
                    <div className="label-mono">Pre-analysis</div>
                    <div className="text-sm">PDF pages detected: <span className="font-mono">{pageCount}</span></div>
                    <div className="flex items-center gap-3 text-sm">
                      {preAnalysis.slug?.found
                        ? <CheckCircle2 size={16} className="text-severity-pass" />
                        : <AlertTriangle size={16} className="text-severity-high" />}
                      <span>Production slug {preAnalysis.slug?.found ? 'detected' : 'not detected — material compliance disclaimer will apply'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      {preAnalysis.dimensions?.found
                        ? <CheckCircle2 size={16} className="text-severity-pass" />
                        : <Info size={16} className="text-severity-medium" />}
                      <span>Pack dimensions {preAnalysis.dimensions?.found ? 'found in artwork' : 'not detected — please enter PDP dimensions below'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      {preAnalysis.keyline?.found
                        ? <CheckCircle2 size={16} className="text-severity-pass" />
                        : <Info size={16} className="text-severity-medium" />}
                      <span>Keyline / dieline {preAnalysis.keyline?.found ? 'detected' : 'not detected — structural checks limited'}</span>
                    </div>
                    {expectedPanels.length > 0 && (
                      <div>
                        <div className="label-mono-sm mb-1">Expected panels for this format</div>
                        <div className="font-mono text-xs text-muted-foreground">{expectedPanels.join(' · ')}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* CONTEXT FORM */}
              <div className="panel p-8 space-y-5" data-testid="context-form">
                <div className="label-mono">Product context</div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="label-mono-sm">Product category</Label>
                    <Select value={ctx.product_category} onValueChange={(v) => setCtx({ ...ctx, product_category: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="ctx-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['General Foods','Beverages','Dairy','Bakery & Snacks','Confectionery & Sweets','Condiments','Sauces','Health Supplements','FSDU','Organic','Alcoholic Beverages','Infant Nutrition','Other'].map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-mono-sm">Net weight</Label>
                    <Input value={ctx.net_weight} onChange={(e) => setCtx({ ...ctx, net_weight: e.target.value })} className="rounded-none mt-1" data-testid="ctx-net-weight" />
                  </div>
                  <div>
                    <Label className="label-mono-sm">Unit</Label>
                    <Select value={ctx.net_weight_unit} onValueChange={(v) => setCtx({ ...ctx, net_weight_unit: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="ctx-net-weight-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['g','kg','ml','l','pcs'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-mono-sm">PDP width (mm)</Label>
                    <Input value={ctx.pdp_width_mm} onChange={(e) => setCtx({ ...ctx, pdp_width_mm: e.target.value })} className="rounded-none mt-1" inputMode="decimal" data-testid="ctx-width" />
                  </div>
                  <div>
                    <Label className="label-mono-sm">PDP height (mm)</Label>
                    <Input value={ctx.pdp_height_mm} onChange={(e) => setCtx({ ...ctx, pdp_height_mm: e.target.value })} className="rounded-none mt-1" inputMode="decimal" data-testid="ctx-height" />
                  </div>

                  <div className="col-span-2 flex items-center justify-between border-t border-border pt-4">
                    <div>
                      <div className="text-sm font-medium">Imported product</div>
                      <div className="text-xs text-muted-foreground">Adds Country of Origin and Importer Address checks</div>
                    </div>
                    <Switch checked={ctx.is_imported} onCheckedChange={(v) => setCtx({ ...ctx, is_imported: v })} data-testid="ctx-imported" />
                  </div>
                  <div className="col-span-2 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">Sold on e-commerce</div>
                      <div className="text-xs text-muted-foreground">Applies e-commerce display checks</div>
                    </div>
                    <Switch checked={ctx.is_ecommerce} onCheckedChange={(v) => setCtx({ ...ctx, is_ecommerce: v })} data-testid="ctx-ecommerce" />
                  </div>

                  {preAnalysis && (!preAnalysis.slug?.found || !preAnalysis.dimensions?.found || !preAnalysis.keyline?.found) && (
                    <div className="col-span-2 border-t border-border pt-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <Label className="label-mono-sm">Material specs <span className="text-muted-foreground normal-case font-normal">(optional)</span></Label>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Pre-analysis couldn't read some fields. Add any structural / material info to improve the audit — e.g.{' '}
                            <span className="font-mono">BOPP/MetPET 12µ + 20µ, 4-colour flexo, matte lamination, GSM 80</span>.
                          </div>
                        </div>
                      </div>
                      <textarea
                        value={ctx.material_specs}
                        onChange={(e) => setCtx({ ...ctx, material_specs: e.target.value })}
                        data-testid="ctx-material-specs"
                        rows={3}
                        className="w-full rounded-none border border-input bg-background p-3 text-sm font-mono leading-relaxed focus:outline-none focus:border-accent transition-colors"
                        placeholder="Substrate, GSM, ink system, finish, dieline notes…"
                      />
                    </div>
                  )}
                </div>

                <div className="border-t border-border pt-5">
                  <Button onClick={startAudit} disabled={!auditId || running} data-testid="run-audit-btn" className="rounded-none w-full h-12">
                    Run full audit
                  </Button>
                  <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
                    By running an audit you acknowledge the PackCheck disclaimer — findings are indicative and do not constitute legal advice.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 4 — Progress */}
        {step === 4 && (
          <div data-testid="step-progress" className="animate-fade-up max-w-3xl mx-auto py-10">
            <div className="label-mono mb-2">Step 04 / Auditing</div>
            <h1 className="text-3xl md:text-4xl font-medium tracking-tight">Running compliance audit</h1>
            <p className="text-muted-foreground mt-3">Pack format-aware multimodal analysis via Claude Sonnet 4.5. This typically takes 30–60 seconds.</p>

            <div className="mt-6 panel p-5 border-gradient-pt flex items-center justify-between gap-4" data-testid="background-notice">
              <div className="text-sm">
                <div className="font-medium">You can keep working.</div>
                <div className="text-muted-foreground text-xs mt-1">This audit keeps running in the background even if you navigate away. Find it in your dashboard with an "In progress" tag.</div>
              </div>
              <button
                onClick={() => nav('/dashboard')}
                data-testid="leave-and-check-dashboard"
                className="shrink-0 inline-flex items-center gap-2 h-10 px-4 border border-border hover:bg-surface-hover text-sm"
              >
                Go to dashboard <ArrowRight size={14} strokeWidth={1.5} />
              </button>
            </div>

            <div className="mt-10 panel p-8">
              <Progress value={(statusInfo.step / 10) * 100} className="h-1 rounded-none" />
              <div className="mt-2 flex items-center justify-between font-mono text-xs text-muted-foreground">
                <span>Step {String(statusInfo.step).padStart(2,'0')} / 10</span>
                <span>{statusInfo.message}</span>
              </div>

              <ol className="mt-8 space-y-3 text-sm">
                {[
                  'PDF received and validated',
                  'Rendering artwork pages',
                  'Identifying PDP, BOP and panel structure',
                  'Reading slug and packaging material data',
                  'Checking keyline structure for manufacturability',
                  'Extracting text, claims, nutrition values',
                  'Analysing font sizes and placement rules',
                  'Checking nomenclature and generic vs invented names',
                  'Running 60+ FSSAI compliance checks',
                  'Report ready',
                ].map((label, idx) => {
                  const n = idx + 1;
                  const active = statusInfo.step === n;
                  const done = statusInfo.step > n;
                  return (
                    <li key={n} className="flex items-center gap-3">
                      <span className={`w-6 h-6 grid place-items-center font-mono text-[10px] relative ${active ? 'text-foreground border-gradient-pt' : done ? 'border border-foreground text-foreground' : 'border border-border text-muted-foreground'}`}>{String(n).padStart(2,'0')}</span>
                      <span className={active ? 'text-foreground' : done ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        )}
      </div>
    </Layout>

      {/* Multi-bundle credit picker — required before audit runs */}
      {bundlePicker.open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-background/70 backdrop-blur-sm" data-testid="bundle-picker-modal">
          <div className="panel p-8 max-w-md w-full mx-4">
            <div className="label-mono mb-2">Choose credit</div>
            <h3 className="text-xl font-medium mb-2">Which credit should this audit consume?</h3>
            <p className="text-sm text-muted-foreground mb-6">You have multiple active bundles. Pick one to continue.</p>
            <div className="space-y-2">
              {bundlePicker.bundles.map((b) => (
                <button
                  key={b.bundle_id}
                  onClick={() => { setBundlePicker({ open: false, bundles: [] }); startAudit(b.bundle_id); }}
                  data-testid={`pick-bundle-${b.type}`}
                  className="w-full text-left p-4 border border-border hover:bg-surface-hover flex items-center justify-between transition-colors"
                >
                  <div>
                    <div className="font-medium capitalize">{b.type.replace(/_/g, ' ')}</div>
                    <div className="font-mono text-[10px] text-muted-foreground mt-1">
                      {b.remaining} of {b.granted} left · {b.expires_at ? `Expires ${new Date(b.expires_at).toLocaleDateString()}` : 'No expiry'}
                    </div>
                  </div>
                  <ArrowRight size={14} strokeWidth={1.5} />
                </button>
              ))}
            </div>
            <button onClick={() => { setBundlePicker({ open: false, bundles: [] }); setRunning(false); }} className="mt-4 text-xs text-muted-foreground underline">Cancel</button>
          </div>
        </div>
      )}
    </>
  );
}
